import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Form, FormGroup, FormControl, Button, Row, Col, Alert } from 'react-bootstrap';
import config from '../../../config';
import { apiFetch } from '../../_services/apiFetch';
import { authenticatedIdentity, recoverSession } from '../../_services/webSession';
import { markPasswordRequired } from '../../_services/savedAccounts';

class ChangePasswordBase extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            npassword: '',
            cpassword: '',
            rpassword: '',
            loading: false,
            success: false,
            error: '',
            user: null
        };
    }

    componentDidMount() {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) {
                return;
            }

            const user = JSON.parse(userData);
            this.setState({ user: user });
        } catch (error) {
            console.error('Error loading user data:', error);
            this.setState({ error: 'Failed to load user data' });
        }
    }

    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value, success: false, error: '' });
    }

    handleSave = () => {
        if (this.state.loading) return;

        const { npassword, cpassword, rpassword, user } = this.state;

        if (!user || !user.id) {
            this.setState({ error: 'User information not found. Please log in again.' });
            return;
        }

        if (!cpassword || !npassword || !rpassword) {
            this.setState({ error: 'Please fill in all password fields.' });
            return;
        }

        if (npassword !== rpassword) {
            this.setState({ error: 'New password and retype password do not match.' });
            return;
        }

        if (!window.confirm("Are you sure you want to change your password?")) {
            return;
        }

        this.setState({ loading: true, success: false, error: '' });
        const identity = authenticatedIdentity();

        apiFetch(`${config.apiUrl}/updatePassword?id=${encodeURIComponent(user.id)}`, {
            expectedIdentity: identity,
            method: "post",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                npassword: npassword,
                cpassword: cpassword,
            })
        })
        .then(async (response) => {
            if (identity !== authenticatedIdentity()) return;

            if (response.status == 200) {
                await response.json();
                markPasswordRequired(user.id);
                const notice = 'Password updated. Please sign in with your new password.';
                recoverSession(identity, notice);
                this.props.navigate('/login', { replace: true, state: { notice } });
            } else if (response.status == 201) {
                this.setState({ 
                    loading: false,
                    error: 'Wrong current password. Please try again.' 
                });
            } else {
                const data = await response.json();
                throw new Error(data.message || 'Failed to update password');
            }
        })
        .catch((error) => {
            console.error('Error updating password:', error);
            this.setState({ 
                loading: false, 
                error: error.message || 'Failed to update password. Please try again.' 
            });
        });
    }

    handleCancel = () => {
        this.setState({
            npassword: '',
            cpassword: '',
            rpassword: '',
            success: false,
            error: ''
        });
    }

    render() {
        const { npassword, cpassword, rpassword, loading, success, error } = this.state;

        try {
            const userData = localStorage.getItem('user');
            if (!userData) {
                return <Navigate to="/login" replace />;
            }
        } catch (error) {
            return <Navigate to="/login" replace />;
        }

        return (
            <div className="container">
                <div style={{ marginTop: '20px', maxWidth: '800px' }}>
                    <div style={{ marginBottom: '30px' }}>
                        <h4>Change Password</h4>
                        <p className="text-muted">Update your account password</p>
                    </div>

                    {success && (
                        <Alert variant="success" onClose={() => this.setState({ success: false })} dismissible>
                            Password updated successfully!
                        </Alert>
                    )}

                    {error && (
                        <Alert variant="danger" onClose={() => this.setState({ error: '' })} dismissible>
                            {error}
                        </Alert>
                    )}

                    <Form>
                        <FormGroup className="row" as={Row} controlId="formCurrentPassword" style={{ marginBottom: '15px' }}>
                            <Col sm={3}>
                                <Form.Label>Current Password</Form.Label>
                            </Col>
                            <Col sm={9}>
                                <FormControl 
                                    type="password" 
                                    name="cpassword"
                                    value={cpassword} 
                                    onChange={this.handleChange}
                                    placeholder="Enter your current password"
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup className="row" as={Row} controlId="formNewPassword" style={{ marginBottom: '15px' }}>
                            <Col sm={3}>
                                <Form.Label>New Password</Form.Label>
                            </Col>
                            <Col sm={9}>
                                <FormControl 
                                    type="password" 
                                    name="npassword"
                                    value={npassword} 
                                    onChange={this.handleChange}
                                    placeholder="Enter your new password"
                                />
                            </Col>
                        </FormGroup>

                        <FormGroup className="row" as={Row} controlId="formRetypePassword" style={{ marginBottom: '15px' }}>
                            <Col sm={3}>
                                <Form.Label>Retype Password</Form.Label>
                            </Col>
                            <Col sm={9}>
                                <FormControl 
                                    type="password" 
                                    name="rpassword"
                                    value={rpassword} 
                                    onChange={this.handleChange}
                                    placeholder="Retype your new password"
                                />
                            </Col>
                        </FormGroup>
                    </Form>

                    <div style={{ marginTop: '30px', display: 'flex', gap: '10px' }}>
                        <Button 
                            variant="primary" 
                            onClick={this.handleSave}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                        <Button 
                            variant="secondary" 
                            onClick={this.handleCancel}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

function ChangePassword(props) {
    const navigate = useNavigate();
    return <ChangePasswordBase {...props} navigate={navigate} />;
}

export { ChangePassword };

